import './new-retailerGM.scss';
import Header from '../../../organisms/header/header.js';
import {Navigate, useParams, useLocation} from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import decodeToken from '../../../../utils/decode-token';
import { Alert, Snackbar } from '@mui/material';
import Grid from "@mui/material/Grid2";
import Input from "../../../atoms/input";
import apiClient from "../../../../utils/apiClient";
import { useForm } from 'react-hook-form';
import Joi from "joi";
import { joiResolver } from "@hookform/resolvers/joi";
import ModalCreateStaff from "../../../organisms/role-admin/modal-create-staff/modal-create-staff";
import ModalAssignDuplicateStaff
  from "../../../organisms/role-admin/modal-assign-duplicate-staff/modal-assign-duplicate-staff";
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import BackButton from '../../../atoms/back-button';

function NewRetailerGM() {
  const { t } = useTranslation();
  const token = sessionStorage.getItem('token') || null;
  const role = decodeToken(token).role;
  const { retailerId } = useParams();
  const location = useLocation();
  const [areaCode, setAreaCode] = useState('');
  const [retailerName, setRetailerName] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [jlrCode] = useState(location.state?.jlr_code || '');
  const [phoneAreaCode] = useState(() => {
    const user = JSON.parse(sessionStorage.getItem('loggedUser'));
    return user?.area_code.toLowerCase() || '';
  });

  const [countries, setCountries] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('');

  const [openModalCreation, setOpenModalCreation] = useState(false);
  const [openModalAssign, setOpenModalAssing] = useState(false);

  const [payload, setPayload] = useState({
    email: '',
    name: '',
    last_name: '',
    prefix_phone: '',
    phone: '',
    preferred_language: '',
    ids_retailer: '',
    retailer_name: ''
  });


  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const onSubmit = async (data) => {

    const fullPhone = parsePhoneNumberFromString(phone);
    if (!fullPhone || !fullPhone.isValid()) {
      setPhoneError(t('alert-phone-format'));
      setFocus('phone');
      return;
    }
    setPhoneError('');

    let newPayload = {
      id: data.id,
      email: data.email,
      name: data.name,
      last_name: data.last_name,
      prefix_phone: data.prefix_phone,
      phone: data.phone,
      preferred_language: data.preferred_language,
      ids_retailer: jlrCode,
      retailer_name: retailerName
    };

    // Realiza la llamada a la API
    try {
      const response = await apiClient.get(`${process.env.REACT_APP_API_URL}/retailer/staff-member?staffEmail=${encodeURIComponent(data.email)}`);

      if (response.status === 200) {
        // Existe
        newPayload = response.data;
        const retailerCodes = response.data.retailerInfo.map(retailer => retailer.retailer_code).join(',');
        newPayload.ids_retailer = `${jlrCode},${retailerCodes}`;
        setPayload(newPayload);
        setOpenModalAssing(true);
      }
      else if (response.status === 204) {
        // No existe
        setPayload(newPayload);
        setOpenModalCreation(true);
      }
    } catch (error) {
      if (error.response?.status === 409) {
        setSnackbarMessage(t('alert-duplicate'));
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      } else {
        setSnackbarOpen(true);
        setSnackbarSeverity('error');
        setSnackbarMessage(t('common-internal-server-error'));

      }
    }
  };



  // Esquema de validación con Joi
  const schema = Joi.object({
    name: Joi.string().required().messages({ 'string.empty': t('alert-name') }),
    last_name: Joi.string().required().messages({ 'string.empty': t('alert-last-name') }),
    email: Joi.string().trim().email({ tlds: { allow: false } }).required().messages({
      'string.email': t('alert-email-format'),
      'string.empty': t('alert-email'),
    }),
    prefix_phone: Joi.string().pattern(/^\+[0-9]{2,3}$/).required().messages({
      'string.pattern.base': t('alert-prefix'),
      'string.empty': t('alert-prefix'),
    }),
    phone: Joi.string().pattern(/^[0-9]{7,15}$/).required().messages({
      'string.pattern.base': t('alert-phone-format'),
      'string.empty': t('alert-phone'),
    }),
    preferred_language: Joi.string().required().messages({ 'string.empty': t('alert-language') }),
  });

  // Uso de react-hook-form y Joi para manejar y validar el formulario
  const { register, handleSubmit, formState, setFocus, setValue  } = useForm({
    resolver: joiResolver(schema),
    mode: 'onBlur'
  });
  const { errors } = formState;

  // Llamada para obtener el área_code del retailer
  useEffect(() => {
    const fetchData = async () => {
      const url = `${process.env.REACT_APP_API_URL}/retailer?retailerId=${retailerId}`;
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache',
        },
      };

      try {
        const response = await apiClient.get(url, config);
        if (response.status === 200 && response.data.retailer.area_code) {
          setAreaCode(response.data.retailer.area_code);
          setRetailerName(response.data.retailer.name);
        }
      } catch (error) {
        setSnackbarOpen(true);
        setSnackbarSeverity('error');
        setSnackbarMessage(t('common-internal-server-error'));
      }
    };

    fetchData();
  }, [retailerId, token, t]);

  // Llamada para obtener los países y establecer el estado de countries
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await apiClient.get(`${process.env.REACT_APP_API_URL}/hac/countries`);
        setCountries(response.data);
      } catch (error) {
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setSnackbarMessage(t('common-internal-server-error'));
      }
    };
    fetchCountries();
  }, [t]);

  // Obtiene los idiomas basados en el areaCode cuando se actualizan countries o areaCode
  useEffect(() => {
    if (countries.length && areaCode) {
      const country = countries.find((country) => country.area_code === areaCode);
      if (country && country.language) {
        setLanguages(country.language);
      }
    }
  }, [countries, areaCode]);

  return (
    <>
      {token && (role === 'admin' || role === 'marketAdmin' || role === 'countryAdmin') ? (
        <div className="App">
          <Header/>
          <main>
            <section className="form">
              <div className="inner">
                <form onSubmit={handleSubmit(onSubmit)}>
                  <BackButton
                    fallbackTo={`/admin/retailer-info/${retailerId}`}
                    context={retailerName}
                  />
                  <h2>
                    {t('admin-new-retailerGM-title')}
                    <small>{t('admin-new-retailerGM-text')}</small>
                  </h2>

                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 6, md: 5 }}>
                      <div className="form-control required">
                        <Input
                          maxLength="50"
                          type="text"
                          id="name"
                          label={t('common-name')}
                          error={errors?.name}
                          {...register('name')}
                        />
                      </div>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 5 }}>
                      <div className="form-control required">
                        <Input
                          maxLength="50"
                          type="text"
                          id="last_name"
                          label={t('common-last-name')}
                          error={errors?.last_name}
                          {...register('last_name')}
                        />
                      </div>
                    </Grid>
                  </Grid>

                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                      <div className="form-control required">
                        <Input
                          maxLength="50"
                          type="text"
                          id="email"
                          label={t('common-email')}
                          error={errors?.email}
                          {...register('email')}
                        />
                      </div>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                      <div className="form-control required">
                        <label htmlFor="phone">{t('common-phone')}</label>
                        <PhoneInput
                          defaultCountry={phoneAreaCode}
                          value={phone}
                          onChange={(value) => {
                            setPhone(value);

                            // Solo validar si ya fue tocado
                            if (phoneTouched) {
                              const parsed = parsePhoneNumberFromString(value);
                              if (parsed && parsed.isValid()) {
                                setPhoneError('');
                                setValue('prefix_phone', `+${parsed.countryCallingCode}`);
                                setValue('phone', parsed.nationalNumber);
                              } else {
                                setPhoneError(t('alert-phone-format'));
                              }
                            }
                          }}
                          onBlur={() => {
                            setPhoneTouched(true);
                            const parsed = parsePhoneNumberFromString(phone);
                            if (!parsed || !parsed.isValid()) {
                              setPhoneError(t('alert-phone-format'));
                            } else {
                              setPhoneError('');
                              setValue('prefix_phone', `+${parsed.countryCallingCode}`);
                              setValue('phone', parsed.nationalNumber);
                            }
                          }}
                        />

                        {phoneError && <span className="error-message">{phoneError}</span>}

                        <input type="hidden" {...register('prefix_phone')} />
                        <input type="hidden" {...register('phone')} />


                        {phoneError && <span className="error-message">{phoneError}</span>}
                      </div>
                    </Grid>
                  </Grid>

                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 1, md: 6 }}>
                      <div className="form-control required">
                        <label htmlFor="preferred_language">{t('common-select-language')}</label>
                        <select
                          name="preferred_language"
                          id="preferred_language"
                          className="normal"
                          {...register('preferred_language')}
                        >
                          <option value="">{t('common-select-language')}</option>
                          {languages.map((language, index) => (
                            <option key={index} value={language}>
                              {t(`languages-${language}`)}
                            </option>
                          ))}
                        </select>
                        {errors.preferred_language && (
                          <span className="input__error">{errors.preferred_language.message}</span>
                        )}
                      </div>
                    </Grid>
                  </Grid>

                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                      <button className="btn">
                        {t('admin-new-retailerGM-action')}
                      </button>
                    </Grid>
                  </Grid>
                </form>
              </div>
            </section>
          </main>
          <ModalCreateStaff
            open={openModalCreation}
            onClose={() => setOpenModalCreation(false)}
            user={payload}
            role="retailerGM"
          />
          <ModalAssignDuplicateStaff
            open={openModalAssign}
            onClose={() => setOpenModalAssing(false)}
            user={payload}
          />
          <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={handleSnackbarClose}>
            <Alert severity={snackbarSeverity} sx={{ width: '100%' }}>
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </div>
      ) : (
        <Navigate to="/login"/>
      )}
    </>
  );
}

export default NewRetailerGM;
